import { Card, CardContent } from "~/components/reusable/Card"
import { formatNumber } from "~/utils/formatting"
import React, { useMemo } from "react"
import { api } from "~/utils/api"
import { type ICollectionDetails, type ICollectionSimple } from "~/hooks/useCollections"
import SuperChart, { type DateChartSeries } from "~/components/reusable/graphs/SuperChart"
import { first, maxBy, minBy, sumBy } from "lodash"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import { filterNotUndefinedOrNull, groupBy } from "~/utils/tsUtils"
import { ShoppingCartSimple } from "@phosphor-icons/react"
import type Highcharts from "highcharts"
import { ChartType, TodayPlotLine } from "~/constants/graphs"
import { formatName } from "~/utils/namingUtils"
import { useYearColor } from "~/constants/colors"
import { useGetCollectionsWithDetails } from "~/hooks/useCollections"
import { MonthTimePoint, groupByMonth, mapToSeason, startOfTodayUTC } from "~/utils/datetime"
import { DateType } from "~/constants/graphs"
import { DateFormatType, formatDate } from "~/utils/datetime"
import { calculateCollectionStatus } from "~/utils/collections"
import { useMultiCollectionSalesForecast } from "~/hooks/useCollectionForecast"

type SalesContentProps = {
  isLoading: boolean
  isLoadingComparison: boolean
  series: DateChartSeries[]
}

const SalesContent = ({ isLoading, isLoadingComparison, series }: SalesContentProps) => {
  if (isLoading || isLoadingComparison) {
    return <LoadingOverlay />
  }

  if (series.length === 0) {
    return (
      <div className="text-center label-lg">
        <p>No {formatName("sale", true)} data available for the selected collection.</p>
      </div>
    )
  }

  return (
    <SuperChart
      type={ChartType.TimeSeries}
      todayPlotLine={TodayPlotLine.Basic}
      height={220}
      data={series}
      dateType={DateType.MonthlyNoYear}
      xAxisLabelFormatter={function (this: Highcharts.AxisLabelsFormatterContextObject) {
        return formatDate(this.value as number, DateFormatType.GraphMonthWithoutYear)
      }}
    />
  )
}

type Props = {
  collections: ICollectionDetails[]
  comparisonCollections?: ICollectionSimple[]
  startDate: Date
  endDate: Date
  warehouseId?: string
  channel?: string
}

function createSeries(
  salesName: string | undefined,
  data: { date: Date; value: number }[],
  type: "spline" | "areaspline" | "column" = "spline",
  colors: string[],
  dashStyle: "Solid" | "Dash" = "Solid",
): DateChartSeries {
  const series: DateChartSeries = {
    name: salesName ?? formatName("sale", true),
    data: groupByMonth(data, { mode: "time" }) as MonthTimePoint[],
    type,
    colors,
    dashStyle,
  }

  return series
}

// Type for grouped comparison collections
type ComparisonGroup = {
  groupName: string
  collections: ICollectionSimple[]
  startDate: Date
  endDate: Date
}

/*
  Showing the sales data for the current collection and the comparison collection groups if available.
  Showing the forecast data for the current collection and each comparison group.
  If all datapoints are zero, it does not show the data for the graph.
  If a collection group is expired, it does not show the forecast data for that group.
  Each comparison collection group (e.g., SS25, SS24) gets its own line with a distinct color.
*/
const CollectionSalesGraphCard = ({
  collections,
  comparisonCollections,
  startDate,
  endDate,
  warehouseId,
  channel,
}: Props) => {
  const getYearColor = useYearColor()
  const externalIds = filterNotUndefinedOrNull(collections.map((c) => c.externalId))

  // Group comparison collections by their group name (e.g., SS25, SS24)
  const comparisonGroups: ComparisonGroup[] = useMemo(() => {
    if (!comparisonCollections || comparisonCollections.length === 0) return []

    const grouped = groupBy(comparisonCollections, (c) => c.groupName ?? c.name ?? "Unknown")

    return Object.entries(grouped).map(([groupName, groupCollections]) => ({
      groupName,
      collections: groupCollections,
      startDate: minBy(groupCollections, (c) => c.startDate)?.startDate ?? startDate,
      endDate: maxBy(groupCollections, (c) => c.endDate)?.endDate ?? endDate,
    }))
  }, [comparisonCollections, startDate, endDate])

  // Fetch sales data for main collection(s)
  const { data: salesData, isLoading } = api.sales.getSalesSeriesForCollectionPeriod.useQuery({
    collectionIds: collections.map((c) => c.id),
    externalCollectionIds: externalIds,
    startDate,
    endDate,
    warehouseId,
    channel,
  })

  // Fetch collection details for comparison collections
  const { data: comparisonCollectionDetails, isLoading: isLoadingComparisonDetails } =
    useGetCollectionsWithDetails({
      collectionIds:
        comparisonCollections && comparisonCollections.length > 0
          ? comparisonCollections.map((c) => c.id)
          : [],
    })

  // Fetch sales data for each comparison group (aggregating all collections in the group)
  const comparisonGroupSalesQueries = api.useQueries((t) =>
    comparisonGroups.map((group) =>
      t.sales.getSalesSeriesForCollectionPeriod(
        {
          collectionIds: group.collections.map((c) => c.id),
          externalCollectionIds: filterNotUndefinedOrNull(
            group.collections.map((c) => c.externalId),
          ),
          startDate: group.startDate,
          endDate: group.endDate,
          warehouseId,
          channel,
        },
        { enabled: comparisonGroups.length > 0 },
      ),
    ),
  )

  const isLoadingComparison =
    isLoadingComparisonDetails || comparisonGroupSalesQueries.some((q) => q.isLoading)

  // Get forecast data using the multi-collection hook (groups by collection group)
  const { forecastData, comparisonGroupForecastsData } = useMultiCollectionSalesForecast(
    collections,
    comparisonCollectionDetails && comparisonCollectionDetails.length > 0
      ? (comparisonCollectionDetails as ICollectionDetails[])
      : [],
    startDate,
    endDate,
    warehouseId,
    channel,
  )

  function isDataAllZero(data: { date: Date; value: number }[]) {
    return data.every((s) => s.value === 0)
  }

  const baseYear = startOfTodayUTC().getUTCFullYear()

  const seasonSales = mapToSeason(
    salesData ?? [],
    salesData?.[0]?.date.getUTCFullYear() ?? baseYear,
  )
  const firstCollection = maxBy(collections, (c) => c.skuCount) ?? first(collections)
  const salesName =
    collections.length > 1 ? firstCollection?.collectionGroup?.name : firstCollection?.name

  const series: DateChartSeries[] = []

  // Add main collection sales series
  if (!isDataAllZero(seasonSales)) {
    const salesSeries = createSeries(salesName, seasonSales, "spline", [getYearColor(0)], "Solid")
    series.push(salesSeries)
  }

  // Add main collection forecast series (if not expired)
  const collectionStatus = calculateCollectionStatus(startDate, endDate)
  if (collectionStatus !== "Expired" && !isDataAllZero(forecastData)) {
    const forecastSalesSeries = createSeries(
      salesName + " Forecast",
      forecastData,
      "spline",
      [getYearColor(0)],
      "Dash",
    )
    series.push(forecastSalesSeries)
  }

  // Add series for each comparison collection group
  comparisonGroups.forEach((group, index) => {
    const color = getYearColor(index + 1)
    const groupName = group.groupName
    const queryResult = comparisonGroupSalesQueries[index]
    const groupSalesData = queryResult?.data

    // Add sales series for this comparison group
    if (groupSalesData && groupSalesData.length > 0 && !isDataAllZero(groupSalesData)) {
      const seasonGroupSales = mapToSeason(
        groupSalesData,
        groupSalesData[0]?.date.getUTCFullYear() ?? baseYear,
      )
      const groupSalesSeries = createSeries(groupName, seasonGroupSales, "spline", [color], "Solid")
      series.push(groupSalesSeries)
    }

    // Check if this group is expired (based on the group's date range)
    const groupStatus = calculateCollectionStatus(group.startDate, group.endDate)

    // Add forecast series for this group (only if not expired)
    if (groupStatus !== "Expired") {
      const forecastDataForGroup = comparisonGroupForecastsData.find(
        (f) => f.groupName === groupName,
      )
      if (
        forecastDataForGroup &&
        forecastDataForGroup.forecastData.length > 0 &&
        !isDataAllZero(forecastDataForGroup.forecastData)
      ) {
        const seasonGroupForecastSales = mapToSeason(
          forecastDataForGroup.forecastData,
          forecastDataForGroup.forecastData[0]?.date.getUTCFullYear() ?? baseYear,
        )
        const groupForecastSalesSeries = createSeries(
          groupName + " Forecast",
          seasonGroupForecastSales,
          "spline",
          [color],
          "Dash",
        )
        series.push(groupForecastSalesSeries)
      }
    }
  })

  const salesSum = useMemo(() => sumBy(salesData, "value"), [salesData])

  const isThereAnyGraphData = useMemo(() => {
    return series.length > 0 && salesSum > 0
  }, [series, salesSum])

  return (
    <Card className="w-full">
      <CardContent className="flex flex-col">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <ShoppingCartSimple className="text-fg-subtle" />
            <div className="label-base text-fg-subtle">{formatName("sale", true, true)}</div>
          </div>

          <div className="flex items-end gap-1">
            <div className="heading-sm">
              {isThereAnyGraphData ? formatNumber(salesSum, 0) : "-"}
            </div>
            <p className="paragraph-base pb-[2px]">Units</p>
          </div>
        </div>
        <div className="flex justify-center items-center w-full h-[220px]">
          <SalesContent
            isLoading={isLoading}
            isLoadingComparison={isLoadingComparison}
            series={series}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export default CollectionSalesGraphCard
